import { Card as AntCard, Space, Typography, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CLAIM_STATUSES, getClaimAmountVisibility } from 'all-in-one-shared-api';

import ClaimTypeIcon from 'components/claimTypeIcon';
import { getClaimPageUrl } from 'utils';
import { StyledTitle } from 'components/StyledTitle';

import ClaimBadge from './ClaimBadge';
import { ShortfallInfo } from './ShortfallInfo';
import { PrimaryButton } from '../../../../components';
import { FormattedClaimData } from '../../utils/formatClaimCardData';
import { ClaimTypesEnum } from '../FilterSection/utils';

const { Text } = Typography;

const ClaimNumberText = styled(Text)`
  color: var(--neutral-7);
`;

const Card = styled(AntCard)`
  border-radius: 0.5rem;
  height: 100%;
`;

const SettleButton = styled(PrimaryButton)`
  margin-top: 1em;
  width: 100%;
`;

type FormattedSettleShortFallData = FormattedClaimData & {
  onSettleShortfall?: (id: string, sequenceNo: number) => void;
};

const ClaimCard = ({
  claimTypeDescription,
  diagnosisDescription,
  claimStatus,
  doctorName,
  treatmentDate,
  claimId,
  sequenceNo,
  referenceNo,
  claimType,
  patientName,
  // shortfallAmount,
  osShortfallAmount,
  daysOverdue,
  onSettleShortfall,
  receiptAmount,
  paidAmount,
  networkClaim,
  shortfallFlag,
}: FormattedSettleShortFallData) => {
  const { t } = useTranslation();
  const settleShortfallClicked = () => {
    onSettleShortfall && onSettleShortfall(claimId || referenceNo || '', sequenceNo);
  };

  const _claimId = claimId || referenceNo;
  const claimDetailsPageUrl = getClaimPageUrl({ claimId, referenceNo, claimStatus, sequenceNo });
  const getTitleTreatmentBasedOnClaimType = (claimType = ClaimTypesEnum.OP) => {
    return claimType === ClaimTypesEnum.MAT || claimType === ClaimTypesEnum.HOSP
      ? 'claims.treatedAdmissionOn'
      : 'claims.treatedOn';
  };
  const { showPaidAmount, showReceiptAmount } = getClaimAmountVisibility({
    networkClaim,
    status: claimStatus,
    claimTypeCode: claimType?.code,
  });

  const getAmount = () => {
    if (claimStatus === CLAIM_STATUSES.COMPLETED) {
      return t('claims.paidAmount', { amount: `${paidAmount?.currency} ${paidAmount?.amount}` });
    }
    return t('claims.receiptAmount', { amount: `${receiptAmount?.currency} ${receiptAmount?.amount}` });
  };

  return (
    <Card
      title={<ClaimNumberText>{`#${_claimId}`}</ClaimNumberText>}
      extra={claimDetailsPageUrl && <Link to={claimDetailsPageUrl}>{t('common.more')}</Link>}
      bordered={false}
    >
      {/* Basic info */}
      <Space align="start">
        <ClaimTypeIcon claimType={claimType} />

        <Space direction="vertical">
          {/* leave N/A in case claimTypeDescription is empty for the consistency of card's height*/}
          <StyledTitle level={5}>{claimTypeDescription || 'N/A'}</StyledTitle>
          <Text>{diagnosisDescription}</Text>
          <ClaimBadge status={shortfallFlag == 'Y' ? 'IN_PROGRESS' : claimStatus} />
          {doctorName && <Text type="secondary">{t('claims.doctor', { doctorName })}</Text>}
          <Text type="secondary">
            {t(getTitleTreatmentBasedOnClaimType(claimType.code as ClaimTypesEnum), { treatmentDate })}
          </Text>
          {((showReceiptAmount && receiptAmount?.amount !== '0') || showPaidAmount) && (
            <>
              <Text type="secondary">{getAmount()}</Text>
            </>
          )}
        </Space>
      </Space>

      {/* Shortfall info */}
      {claimStatus === CLAIM_STATUSES.SHORTFALL && (
        <>
          <Divider type="horizontal" />
          <ShortfallInfo label={t('claims.shortfall.claimCard.patientName')} value={patientName || ''} />
          <ShortfallInfo
            label={t('claims.shortfall.claimCard.shortfallAmount')}
            value={osShortfallAmount ? `${osShortfallAmount.currency} ${osShortfallAmount.amount}` : ''}
            isValueRed
          />
          {daysOverdue && (
            <ShortfallInfo
              label={t('claims.shortfall.claimCard.daysOverdue')}
              value={`${daysOverdue}`}
              isValueRed={!!daysOverdue}
            />
          )}
          <SettleButton color="var(--primary-color)" disabled={shortfallFlag === 'Y'} onClick={settleShortfallClicked}>
            {t('claims.shortfall.settleShortfall')}
          </SettleButton>
        </>
      )}
    </Card>
  );
};

export default ClaimCard;
